import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Globe, ArrowRight } from "lucide-react";
import { api } from "@/platform/api";
import { useAuth } from "@/platform/AuthContext";
import { FullScreenLoader } from "@/platform/components/primitives";
import GettingStarted from "@/platform/app/GettingStarted";
import NewProjectModal from "@/platform/app/NewProjectModal";
import ReferralSurveyModal from "@/platform/app/ReferralSurveyModal";

function mergeChecklist(projects) {
  const any = (k) => projects.some((p) => p.checklist?.[k]);
  return {
    create_project: projects.length > 0,
    connect_pinlayr: any("connect_pinlayr"),
    first_pin: any("first_pin"),
    customize_widget: any("customize_widget"),
    invite_team: any("invite_team"),
    connect_workflow: any("connect_workflow"),
  };
}

export default function Overview() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(false);
  const [survey, setSurvey] = useState(false);

  const load = async () => {
    try { const d = await api.get("/api/projects"); setItems(d.projects || []); }
    catch (_e) { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  // Survey now fires AFTER the first project exists (not on first /app load).
  useEffect(() => {
    if (items && items.length > 0 && user && !user.referralSource) setSurvey(true);
  }, [items, user]);

  const onCreated = (res) => { setModal(false); navigate(`/app/projects/${res.project.id}`); };

  if (items === null) return <FullScreenLoader label="Loading your workspace" />;
  const checklist = mergeChecklist(items);
  const allDone = Object.values(checklist).every(Boolean);

  return (
    <div className="animate-in fade-in duration-300">
      {items.length === 0 ? (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="rounded-2xl border border-[#242A38] bg-[#151A24] p-10 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl" style={{ background: "rgba(124,107,255,0.18)" }}><Globe className="h-7 w-7" style={{ color: "#A99BFF" }} /></div>
            <h1 className="mt-6 text-[26px] font-semibold tracking-tight text-[#F4F5F8]" data-testid="welcome-heading">Welcome to Pinlayr</h1>
            <p className="mt-2 text-[15px] text-[#98A0B0]">Get your first website ready for review.</p>
            <button onClick={() => setModal(true)} className="mt-7 inline-flex items-center gap-2 rounded-lg bg-[#6D5EF6] px-6 py-3 text-[14px] font-semibold text-white hover:bg-[#5B4CE0]" data-testid="create-project-btn"><Plus className="h-4 w-4" /> Create your first project</button>
          </div>
          <GettingStarted checklist={checklist} />
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div>
            <div className="mb-5 flex items-center justify-between">
              <h1 className="text-[22px] font-semibold tracking-tight text-[#F4F5F8]">Projects</h1>
              <button onClick={() => setModal(true)} className="inline-flex items-center gap-2 rounded-lg bg-[#6D5EF6] px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-[#5B4CE0]" data-testid="create-project-btn"><Plus className="h-4 w-4" /> New project</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map(({ project, sites, counts }) => (
                <button key={project.id} onClick={() => navigate(`/app/projects/${project.id}`)} className="group rounded-2xl border border-[#242A38] bg-[#151A24] p-5 text-left transition-shadow hover:shadow-md" data-testid="project-card">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h3 className="truncate text-[15px] font-semibold text-[#F4F5F8]">{project.name}</h3>
                      <p className="mt-0.5 truncate text-[13px] text-[#6E7686]">{sites?.[0]?.domain || "—"}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-[#565E70] transition-colors group-hover:text-[#F4F5F8]" />
                  </div>
                  <div className="mt-4 flex gap-4 text-[12.5px] text-[#98A0B0]">
                    <span><b className="text-[#F4F5F8]">{counts.open}</b> Open</span>
                    <span><b className="text-[#F4F5F8]">{counts.ready}</b> Ready</span>
                    <span><b className="text-[#F4F5F8]">{counts.resolved}</b> Resolved</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          {!allDone && <GettingStarted checklist={checklist} />}
        </div>
      )}

      {modal && <NewProjectModal onClose={() => setModal(false)} onCreated={onCreated} />}
      {survey && <ReferralSurveyModal onDone={() => { setSurvey(false); refresh(); }} />}
    </div>
  );
}
